import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import {
  getAccessToken,
  getRefreshToken,
  clearAuthData,
  getJwtExpiryMs,
} from "@b2b/services";
import { SellerStatus } from "@b2b/services";

export type UserStatus = "not_registered" | "pending_registration" | "active";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * True while the first GET /me after a token is present is in flight (or after refetchUserStatus).
   * Route guards should wait so incomplete OAuth signups cannot access dashboards before status is known.
   */
  isSessionProfileLoading: boolean;
  /** From GET /me — used when redirecting incomplete signups to `/login?tab=signup` with prefilled email. */
  sessionEmail?: string;
  /** From GET /me - only set after fetch. Used to show signup form for OAuth users until they complete registration. */
  userStatus?: UserStatus;
  userRole?: "buyer" | "seller" | "admin";
  companyId?: string;
  sellerStatus?: SellerStatus | null;
  profileCompleteWithoutSocial?: boolean;
  /** Seller: 0-100. At 80%+ can access dashboard, analytics, chat. */
  profileCompletionPercentage?: number;
  /** Seller: true if membership plan selected (onboarding Step 2). */
  hasMembershipPlanSelected?: boolean;
  /** Seller: true if storefront complete (onboarding Step 3). */
  storefrontComplete?: boolean;
  /**
   * STAFF-only: modules this user may access.
   * `undefined` for Super Admin / Company Admin (full catalog).
   */
  allowedModules?: string[];
  login: () => void;
  logout: () => void;
  /** Call after registration/sync so userStatus updates to "active". */
  refetchUserStatus: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [hasToken, setHasToken] = useState<boolean>(false);
  const [isCheckingToken, setIsCheckingToken] = useState<boolean>(true);
  const [isSessionProfileLoading, setIsSessionProfileLoading] =
    useState<boolean>(false);
  const [sessionEmail, setSessionEmail] = useState<string | undefined>();
  const [userStatus, setUserStatus] = useState<UserStatus | undefined>();
  const [userRole, setUserRole] = useState<"buyer" | "seller" | "admin">();
  const [companyId, setCompanyId] = useState<string>();
  const [sellerStatus, setSellerStatus] = useState<SellerStatus | null | undefined>();
  const [profileCompleteWithoutSocial, setProfileCompleteWithoutSocial] = useState<boolean | undefined>();
  const [profileCompletionPercentage, setProfileCompletionPercentage] = useState<number | undefined>();
  const [hasMembershipPlanSelected, setHasMembershipPlanSelected] = useState<boolean | undefined>();
  const [storefrontComplete, setStorefrontComplete] = useState<boolean | undefined>();
  const [allowedModules, setAllowedModules] = useState<string[] | undefined>();
  const isFetchingRef = useRef(false);
  const hasFetchedRef = useRef(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const isAuthenticated = hasToken;
  const isLoading = isCheckingToken;

  const refetchUserStatus = () => {
    hasFetchedRef.current = false;
    isFetchingRef.current = false;
    setFetchTrigger((t) => t + 1);
  };

  // Check for token in localStorage on mount and on storage events
  useEffect(() => {
    let checkCount = 0;
    let slowInterval: ReturnType<typeof setInterval> | null = null;

    const checkToken = async () => {
      try {
        const token = await getAccessToken();
        const nowHasToken = !!token;
        setHasToken(nowHasToken);
        if (checkCount === 0) {
          setIsCheckingToken(false);
        }
      } catch {
        setHasToken(false);
      } finally {
        if (checkCount === 0) {
          setIsCheckingToken(false);
        }
      }
    };

    checkToken();
    window.addEventListener("storage", (e: StorageEvent) => {
      if (e.key === "bt_b2b_access_token") checkToken();
    });
    // Same-tab token updates (e.g. after signup) - storage event doesn't fire in same tab
    const onTokenUpdated = () => { checkToken(); };
    window.addEventListener("bt_b2b_token_updated", onTokenUpdated);

    const fastInterval = setInterval(() => {
      checkToken();
      checkCount++;
      if (checkCount >= 20 && !slowInterval) {
        clearInterval(fastInterval);
        slowInterval = setInterval(checkToken, 5000);
      }
    }, 100);

    return () => {
      window.removeEventListener("storage", checkToken);
      window.removeEventListener("bt_b2b_token_updated", onTokenUpdated);
      clearInterval(fastInterval);
      if (slowInterval) clearInterval(slowInterval);
    };
  }, []);

  // Fetch user role and profile completion when we have a token (or after refetchUserStatus)
  useEffect(() => {
    if (!hasToken) {
      hasFetchedRef.current = false;
      isFetchingRef.current = false;
      setIsSessionProfileLoading(false);
      setSessionEmail(undefined);
      setUserStatus(undefined);
      setUserRole(undefined);
      setCompanyId(undefined);
      setSellerStatus(undefined);
      setProfileCompleteWithoutSocial(undefined);
      setProfileCompletionPercentage(undefined);
      setHasMembershipPlanSelected(undefined);
      setStorefrontComplete(undefined);
      setAllowedModules(undefined);
      return;
    }

    if (!isFetchingRef.current && !hasFetchedRef.current) {
      isFetchingRef.current = true;
      setIsSessionProfileLoading(true);

      const fetchUserRole = async () => {
        try {
          const token = await getAccessToken();
          if (!token) {
            hasFetchedRef.current = true;
            setUserStatus(undefined);
            setSessionEmail(undefined);
            return;
          }
          const { authService } = await import("@b2b/services");
          const meResponse = await authService.getMe();
          hasFetchedRef.current = true;
          setUserStatus(meResponse.status as UserStatus);
          setSessionEmail(meResponse.user?.email?.trim() || undefined);

          // Set role from user for both "active" and "pending_registration" so sellers can access profile_settings after signup
          if (meResponse.user?.role) {
            const role = meResponse.user.role.toLowerCase();
            if (role === "super_admin" || role === "company_admin" || role === "staff") {
              setUserRole("admin");
            } else if (role === "seller" || role === "buyer_seller") {
              setUserRole("seller");
            } else {
              setUserRole("buyer");
            }
            setCompanyId(meResponse.user.companyId ?? undefined);
            setSellerStatus(meResponse.user.sellerStatus ?? null);
            setProfileCompleteWithoutSocial(meResponse.user?.profileCompleteWithoutSocial);
            setProfileCompletionPercentage(meResponse.user?.profileCompletionPercentage);
            setHasMembershipPlanSelected(meResponse.user?.hasMembershipPlanSelected);
            setStorefrontComplete(meResponse.user?.storefrontComplete);
            setAllowedModules(
              role === "staff" ? meResponse.user.allowedModules || [] : undefined,
            );
          } else {
            setUserRole("buyer");
            setCompanyId(undefined);
            setSellerStatus(null);
            setProfileCompleteWithoutSocial(undefined);
            setProfileCompletionPercentage(undefined);
            setHasMembershipPlanSelected(undefined);
            setStorefrontComplete(undefined);
            setAllowedModules(undefined);
          }
        } catch {
          hasFetchedRef.current = true;
          setUserStatus(undefined);
          setSessionEmail(undefined);
          setUserRole("buyer");
          setCompanyId(undefined);
          setSellerStatus(null);
          setProfileCompleteWithoutSocial(undefined);
          setProfileCompletionPercentage(undefined);
          setHasMembershipPlanSelected(undefined);
          setStorefrontComplete(undefined);
          setAllowedModules(undefined);
        } finally {
          isFetchingRef.current = false;
          setIsSessionProfileLoading(false);
        }
      };
      void fetchUserRole();
    }
  }, [hasToken, fetchTrigger]);

  // Auto logout immediately when refresh token expires.
  useEffect(() => {
    let mounted = true;

    const tick = () => {
      const refreshToken = getRefreshToken();
      if (!mounted || !refreshToken) return;
      const expMs = getJwtExpiryMs(refreshToken);
      if (!expMs) return;
      if (expMs <= Date.now()) {
        clearAuthData();
        setUserStatus(undefined);
        setUserRole(undefined);
        setCompanyId(undefined);
        setSellerStatus(undefined);
        setProfileCompleteWithoutSocial(undefined);
        setProfileCompletionPercentage(undefined);
        setHasMembershipPlanSelected(undefined);
        setStorefrontComplete(undefined);
        setAllowedModules(undefined);
        setSessionEmail(undefined);
        setIsSessionProfileLoading(false);
        setHasToken(false);
      }
    };

    tick();
    const intervalId = setInterval(tick, 1000);
    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, []);

  const login = () => {
    window.location.href = "/login";
  };

  const logout = () => {
    clearAuthData();
    setUserStatus(undefined);
    setSessionEmail(undefined);
    setIsSessionProfileLoading(false);
    setUserRole(undefined);
    setCompanyId(undefined);
    setSellerStatus(undefined);
    setProfileCompleteWithoutSocial(undefined);
    setProfileCompletionPercentage(undefined);
    setHasMembershipPlanSelected(undefined);
    setStorefrontComplete(undefined);
    setAllowedModules(undefined);
    setHasToken(false);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        isSessionProfileLoading,
        sessionEmail,
        userStatus,
        userRole,
        companyId,
        sellerStatus,
        profileCompleteWithoutSocial,
        profileCompletionPercentage,
        hasMembershipPlanSelected,
        storefrontComplete,
        allowedModules,
        login,
        logout,
        refetchUserStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};

export const useAuthSafe = () => {
  return useContext(AuthContext);
};
